import type { SairAccount } from "@opencode/schema/sair-account"
import { onCleanup } from "solid-js"
import { createStore } from "solid-js/store"
import type { ServerApi } from "@/runtime/server/api"

export function createSairAccount(input: {
  api: () => ServerApi
  openExternal: (url: string) => unknown
  reconnect?: (signal: AbortSignal) => Promise<unknown>
}) {
  const [state, setState] = createStore<{
    status?: SairAccount.Status
    busy: boolean
    checking: boolean
    loadError: boolean
    actionError: boolean
    revokeError: boolean
  }>({ busy: false, checking: false, loadError: false, actionError: false, revokeError: false })
  const api = () => input.api().sairAccount
  const abort = new AbortController()
  const lifecycle = { disposed: false, generation: 0, loading: undefined as Promise<void> | undefined }

  function refresh(): Promise<void> {
    if (state.busy || lifecycle.disposed) return Promise.resolve()
    if (lifecycle.loading) return lifecycle.loading
    const generation = lifecycle.generation
    const deadline = new AbortController()
    // Allow Core's bounded identity check to finish before transport recovery.
    // Status, reconnect and the replacement status share one overall budget.
    const timeout = setTimeout(() => deadline.abort(), 30_000)
    const signal = AbortSignal.any([abort.signal, deadline.signal])
    const interrupted = Promise.withResolvers<undefined>()
    const interrupt = () => interrupted.resolve(undefined)
    signal.addEventListener("abort", interrupt, { once: true })
    setState("checking", true)
    const check = async () => {
      const first = await api()
        .status({ signal })
        .catch(() => undefined)
      // Recovery must work before sign-in enables the event stream, and the
      // replacement connection must be checked in this same attempt.
      if (first || signal.aborted || generation !== lifecycle.generation || !input.reconnect) return first
      await input.reconnect(signal)
      if (signal.aborted || generation !== lifecycle.generation) return
      return api().status({ signal })
    }
    // Desktop IPC cannot always cancel an in-flight reconnect. Stop waiting at
    // the deadline even if that operation ignores the cancellation signal.
    lifecycle.loading = Promise.race([check().catch(() => undefined), interrupted.promise])
      .then((status) => {
        if (lifecycle.disposed || generation !== lifecycle.generation) return
        if (!status || signal.aborted) {
          setState("loadError", true)
          return
        }
        setState({ status, loadError: false })
      })
      .finally(() => {
        signal.removeEventListener("abort", interrupt)
        clearTimeout(timeout)
        lifecycle.loading = undefined
        if (!lifecycle.disposed) setState("checking", false)
      })
    return lifecycle.loading
  }

  async function action(run: (signal: AbortSignal) => Promise<SairAccount.Status>, limit: number | null = 30_000) {
    if (state.busy) return
    lifecycle.generation += 1
    setState({ busy: true, actionError: false })
    const deadline = new AbortController()
    const timeout = limit === null ? undefined : setTimeout(() => deadline.abort(), limit)
    const signal = AbortSignal.any([abort.signal, deadline.signal])
    const status = await run(signal)
      .catch(() => undefined)
      .finally(() => clearTimeout(timeout))
    if (lifecycle.disposed) return
    setState("busy", false)
    if (!status || signal.aborted) {
      setState("actionError", true)
      return
    }
    setState({ status, loadError: false })
    return status
  }

  // This polls the connected OMM service, never SAIR's limited website endpoints.
  const timer = setInterval(() => {
    // Keep an initial failure stable until the user retries. Established
    // accounts still recover automatically without unmounting their work.
    if (state.loadError && state.status?.auth !== "authenticated") return
    void refresh()
  }, 2500)
  void refresh()
  onCleanup(() => {
    lifecycle.disposed = true
    abort.abort()
    clearInterval(timer)
  })

  return {
    state,
    // A lost connection does not prove logout. Keep drafts mounted until the service reports it.
    authenticated: () => state.status?.auth === "authenticated",
    refresh,
    async start() {
      const status = await action((signal) => api().start({ clientName: "OMM Code" }, { signal }))
      if (status?.attempt) input.openExternal(status.attempt.verificationUrl)
    },
    reopen() {
      const url = state.status?.attempt?.verificationUrl
      if (url) input.openExternal(url)
    },
    cancel() {
      const attempt = state.status?.attempt
      if (!attempt) return Promise.resolve()
      return action((signal) => api().cancel({ attemptID: attempt.attemptID }, { signal }))
    },
    retry: () => {
      if (state.checking) return lifecycle.loading
      if (!state.loadError) return action((signal) => api().retry({ signal }))
      setState("loadError", false)
      return refresh()
    },
    async logout() {
      // Stopping the service's running tasks may outlast a sign-in check.
      await action(async (signal) => {
        const result = await api().logout({ signal })
        setState("revokeError", !result.revoked && result.status.auth === "signed_out")
        return result.status
      }, null)
    },
  }
}

export type SairAccountContext = ReturnType<typeof createSairAccount>
